import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { parseApiError } from '../api/api-error.util';
import { ApiEnvelope, WaitlistSignup } from '../api/api.types';
import { CsrfService } from './csrf.service';

export type WaitlistState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Registers a visitor for the launch announcement.
 *
 * The endpoint is public but the API is stateful, so the XSRF cookie has to be
 * in place before the POST or it comes back 419.
 */
@Injectable({ providedIn: 'root' })
export class WaitlistService {
  private readonly http = inject(HttpClient);
  private readonly csrf = inject(CsrfService);

  readonly state = signal<WaitlistState>('idle');
  readonly error = signal('');

  join(signup: WaitlistSignup): Observable<void> {
    this.state.set('submitting');
    this.error.set('');

    return this.csrf.prime().pipe(
      switchMap(() =>
        this.http.post<ApiEnvelope<null>>(`${environment.apiUrl}/core/public/waitlist`, {
          ...signup,
          source: signup.source ?? 'portal_gate',
        })
      ),
      tap(() => this.state.set('success')),
      map(() => undefined),
      catchError((err: unknown) => {
        this.error.set(
          parseApiError(err, 'We could not add you to the list. Please try again in a moment.')
        );
        this.state.set('error');
        return throwError(() => err);
      })
    );
  }

  /** Lets the form return to an editable state after a failed attempt. */
  reset(): void {
    this.state.set('idle');
    this.error.set('');
  }
}
